import { Skeleton } from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { styled } from "@mui/material/styles";
import { RateType, UnitType } from "@sinch/core/entities/serverEnums";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { head, mapObjIndexed, mergeDeepRight, omit, values } from "ramda";
import { isNotNil, isNull } from "ramda-adjunct";
import React, { FC, memo, Suspense } from "react";
import { DeepPartial, FieldPathValue, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMuiController } from "@sinch/components";
import {
  breakPenaltySettings,
  isBreakPenaltySourcedComponent,
  isBreakPenaltyUnit,
} from "@sinch/components/application/Rate/useIntervalType";
import { useUnitType } from "@sinch/components/application/Rate/useUnitType";
import { IntervalSelect } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/IntervalSelect";
import { RateIntervals } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.graphql";
import { RateIntervalsQuery } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.types";
import { RateComponentInput } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateComponentInput/RateComponentSelectInput";
import { RateInput } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateInput/RateInput";
import {
  RateBuilderTreeFormFields,
  rateRateSchema,
} from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { RateBuilderRowContentItem } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/RateBuilderRowContent";

interface RateInputsProps {
  rowId: string;
  fullWidth?: boolean;
}

export const RateInputs: FC<RateInputsProps> = ({ rowId, fullWidth }) => {
  const { field } = useMuiController<RateBuilderTreeFormFields, `Rate.${string}.rate`>({
    name: `Rate.${rowId}.rate`,
    disableAsReadOnly: true,
  });

  const availableRateComponents = values(
    mapObjIndexed(({ rate }) => rate?.component, useFormContext<RateBuilderTreeFormFields>().getValues().Rate)
  ).filter(isNotNil);

  return (
    <RateInputsControllable
      {...omit(["inputRef"], field)}
      availableRateComponents={availableRateComponents}
      fullWidth={fullWidth}
    />
  );
};

type RateFieldType = Omit<
  ReturnType<typeof useMuiController<RateBuilderTreeFormFields, `Rate.${string}.rate`>>["field"],
  "inputRef"
> & {
  fullWidth?: boolean;
  availableRateComponents?: string[];
};

const RateInputsControllable = memo(
  ({ value, onChange, fullWidth, availableRateComponents, ...rateField }: RateFieldType) => {
    const { t } = useTranslation();
    const { rateType } = useRateBuilderSettings();
    const { getUnitSymbol, getRateName, getUnitName } = useUnitType(rateType);
    const { data: intervalsData } = useQueryHandler<RateIntervalsQuery>(RateIntervals, { ratelistType: rateType });
    // break penalty rows are valued from the rate active during the missed break, the amount input is meaningless
    const isBreakPenalty = isBreakPenaltyUnit(intervalsData?.rateIntervals, value?.unit);
    // ... and so is the component input when the interval reports the penalty under the component it is valued from
    const isCalculatedComponent = isBreakPenaltySourcedComponent(intervalsData?.rateIntervals, value?.unit);
    const modifyVal = (val: DeepPartial<FieldPathValue<RateBuilderTreeFormFields, `Rate.${string}.rate`>>) =>
      mergeDeepRight(mergeDeepRight(rateRateSchema.getDefault(), value ?? {}))(val ?? {}) as FieldPathValue<
        RateBuilderTreeFormFields,
        `Rate.${string}.rate`
      >;

    return (
      <>
        <RateBuilderRowContentItem className="RateBuilderRowContentItem-half" size={fullWidth ? 4 : 3}>
          <Suspense fallback={<Skeleton sx={{ height: "100%", backgroundColor: "grey.300" }} variant="rounded" />}>
            <IntervalSelect
              onChange={(val) => {
                const isFlatTypeChanged =
                  (val?.unit.type === UnitType.Flat && value?.unit.type !== UnitType.Flat) ||
                  (val?.unit.type !== UnitType.Flat && value?.unit.type === UnitType.Flat);

                const unitName = isNotNil(value?.unit.type) ? getUnitName(value?.unit.type) : null;
                const newUnitName = isNotNil(val?.unit.type) ? getUnitName(val?.unit.type) : null;
                const isUnitNameChanged = unitName !== newUnitName;

                const penaltySettings = breakPenaltySettings(intervalsData?.rateIntervals, val?.unit);
                const isBreakPenaltySelected = penaltySettings !== null;
                // the row keeps a component even when the penalty is reported under the rate it is valued from,
                // the builder and the evaluator both need one, so the first penalized component stands in for it
                const calculatedComponent =
                  penaltySettings?.useCustomComponent === false ? head(penaltySettings.componentIds ?? []) : undefined;
                const newVal = isNull(val)
                  ? null
                  : modifyVal({
                      ...val,
                      ...(isFlatTypeChanged || isUnitNameChanged || isBreakPenaltySelected
                        ? {
                            amount: 0,
                            baseComponent: null,
                            type: RateType.Simple,
                          }
                        : {}),
                      ...(isNotNil(calculatedComponent) ? { component: calculatedComponent } : {}),
                    });
                onChange(newVal);
              }}
              // @ts-ignore fix later
              value={value}
              {...rateField}
            />
          </Suspense>
        </RateBuilderRowContentItem>
        <RateBuilderRowContentItem
          className="RateBuilderRowContentItem-half"
          size={fullWidth ? 4 : 2}
          sx={{ minWidth: 200 }}
        >
          {isNotNil(value?.unit.type) &&
            (isBreakPenalty ? (
              <CalculatedInput disabled fullWidth value={t("RateBuilder.calculated")} />
            ) : (
              <Suspense fallback={<Skeleton sx={{ height: "100%", backgroundColor: "grey.300" }} variant="rounded" />}>
                <RateInput
                  {...rateField}
                  availableRateComponents={availableRateComponents}
                  onChange={(val) => onChange(modifyVal(val))}
                  rateName={value?.unit.type === UnitType.Flat ? null : getRateName(value?.unit.type)}
                  unit={value?.unit.type === UnitType.Flat ? null : getUnitSymbol(value?.unit.type)}
                  unitName={getUnitName(value?.unit.type)}
                  value={value}
                />
              </Suspense>
            ))}
        </RateBuilderRowContentItem>
        <RateBuilderRowContentItem size={fullWidth ? 4 : 2} sx={{ minWidth: 200 }}>
          {isNotNil(value?.unit.type) &&
            (isCalculatedComponent ? (
              <CalculatedInput disabled fullWidth value={t("RateBuilder.calculated")} />
            ) : (
              <Suspense fallback={<Skeleton sx={{ height: "100%", backgroundColor: "grey.300" }} variant="rounded" />}>
                <RateComponentInput
                  onChange={(val) => onChange(modifyVal({ component: val ? String(val) : undefined }))}
                  value={value?.component}
                  {...rateField}
                  name={`${rateField.name}.component`}
                />
              </Suspense>
            ))}
        </RateBuilderRowContentItem>
      </>
    );
  }
);
RateInputsControllable.displayName = "RateInputsControllable";

/**
 * Stand-in for inputs the evaluator fills in, matching the height of the row's real inputs
 */
const CalculatedInput = styled(OutlinedInput)(({ theme }) => ({
  height: theme.shape.inputMediumHeight,
}));
